import { createSlice } from "@reduxjs/toolkit";
import {
  getRespondentsByCaseIDAction,
  createRespondentDetailsAction,
  updateRespondentDetailsAction,
  deleteRespondentDetailsAction,
  getRespondentDetailsAction,
  uploadRespondentDocumentAction,
  updateRespondentDocumentAction,
  deleteRespondentDocumentAction,
  getRespondentDocumentsAction,
} from "../../actions/RespondentDetails/respondentDetailsAction";

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  respondents: [],
  respondentDetails: null,
  documents: [],
  loading: false,
  error: null,
  success: false,
  successMessage: "",
};

// ============================================================================
// SLICE
// ============================================================================

const respondentDetailsSlice = createSlice({
  name: "respondentDetails",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.successMessage = "";
    },
    resetRespondentDetails: (state) => {
      state.respondentDetails = null;
    },
  },
  extraReducers: (builder) => {
    // Create Respondent Details
    builder
      .addCase(createRespondentDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRespondentDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Respondent created successfully";
        state.respondents.push(action.payload.data);
      })
      .addCase(createRespondentDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Respondent Details
    builder
      .addCase(updateRespondentDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRespondentDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Respondent updated successfully";
        const index = state.respondents.findIndex(
          (r) =>
            r.respondentDetailsID === action.payload.data.respondentDetailsID,
        );
        if (index !== -1) {
          state.respondents[index] = action.payload.data;
        }
      })
      .addCase(updateRespondentDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Respondent Details
    builder
      .addCase(deleteRespondentDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRespondentDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Respondent deleted successfully";
        state.respondents = state.respondents.filter(
          (r) => r.respondentDetailsID !== action.payload,
        );
      })
      .addCase(deleteRespondentDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Respondent Details
    builder
      .addCase(getRespondentDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRespondentDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.respondentDetails = action.payload.data;
      })
      .addCase(getRespondentDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get All Respondents for a Case
    builder
      .addCase(getRespondentsByCaseIDAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRespondentsByCaseIDAction.fulfilled, (state, action) => {
        state.loading = false;
        state.respondents = action.payload.data || [];
      })
      .addCase(getRespondentsByCaseIDAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Upload Document
    builder
      .addCase(uploadRespondentDocumentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadRespondentDocumentAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Document uploaded successfully";
        state.documents = [
          ...state.documents,
          ...(Array.isArray(action.payload.data)
            ? action.payload.data
            : [action.payload.data]),
        ];
      })
      .addCase(uploadRespondentDocumentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Document
    builder
      .addCase(updateRespondentDocumentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRespondentDocumentAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Document updated successfully";
        const index = state.documents.findIndex(
          (d) => d.documentID === action.payload.data.documentID,
        );
        if (index !== -1) {
          state.documents[index] = action.payload.data;
        }
      })
      .addCase(updateRespondentDocumentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Document
    builder
      .addCase(deleteRespondentDocumentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRespondentDocumentAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = "Document deleted successfully";
        state.documents = state.documents.filter(
          (d) => d.documentID !== action.payload,
        );
      })
      .addCase(deleteRespondentDocumentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Documents
    builder
      .addCase(getRespondentDocumentsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRespondentDocumentsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload.data || [];
      })
      .addCase(getRespondentDocumentsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess, resetRespondentDetails } =
  respondentDetailsSlice.actions;
export default respondentDetailsSlice.reducer;
